import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Tab, Tabs, Typography, useTheme } from "@mui/material";
import * as XLSX from "xlsx";
import {
  ViewerSkeleton,
  ViewerErrorState,
} from "../FileViewerStates/FileViewerStates";
import {
  ViewerZoomToolbar,
  useViewerZoom,
  useViewerFullscreen,
} from "../FileViewerStates/FileViewerToolbar";

const XlsxViewer = ({ fileUrl, fillHeight = false }) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);

  const { scale, zoomIn, zoomOut, reset } = useViewerZoom();
  const { isFullScreen, toggle: toggleFullscreen } =
    useViewerFullscreen(containerRef);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setWorkbook(null);
    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: "array" });
        setWorkbook(wb);
        setActiveSheet(0);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load spreadsheet");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileUrl, retryKey]);

  const rows = useMemo(() => {
    if (!workbook) return [];
    const sheetName = workbook.SheetNames[activeSheet];
    if (!sheetName) return [];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  }, [workbook, activeSheet]);

  const maxCols = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.length), 0),
    [rows]
  );

  const borderColor = theme.customColors?.border?.main ?? "#E8EAED";
  const headerBg = theme.customColors?.background?.tableRow ?? "#F7F7FC";

  if (loading) {
    return (
      <Box sx={{ height: fillHeight ? "100%" : "500px", bgcolor: "#fff" }}>
        <ViewerSkeleton />
      </Box>
    );
  }

  if (error || !workbook) {
    return (
      <Box sx={{ height: fillHeight ? "100%" : "500px", bgcolor: "#fff" }}>
        <ViewerErrorState onRetry={() => setRetryKey((k) => k + 1)} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        height: fillHeight ? "100%" : "500px",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {workbook.SheetNames.length > 1 && (
        <Tabs
          value={activeSheet}
          onChange={(_, v) => setActiveSheet(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${borderColor}`,
            minHeight: 36,
            flexShrink: 0,
            "& .MuiTab-root": {
              minHeight: 36,
              textTransform: "none",
              fontSize: "0.8125rem",
              py: 0.5,
            },
          }}
        >
          {workbook.SheetNames.map((n, i) => (
            <Tab key={n} label={n} value={i} />
          ))}
        </Tabs>
      )}

      <Box sx={{ flex: 1, overflow: "auto", pb: 7 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              This sheet is empty
            </Typography>
          </Box>
        ) : (
          <table
            style={{
              borderCollapse: "collapse",
              width: "max-content",
              minWidth: "100%",
              fontSize: `${13 * scale}px`,
              fontFamily:
                "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  <td
                    style={{
                      border: `1px solid ${borderColor}`,
                      padding: "6px 8px",
                      background: headerBg,
                      color: "#6B7280",
                      fontSize: "11px",
                      fontWeight: 500,
                      textAlign: "right",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                    }}
                  >
                    {ri + 1}
                  </td>
                  {Array.from({ length: maxCols }, (_, ci) => row[ci]).map(
                    (cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          border: `1px solid ${borderColor}`,
                          padding: "6px 10px",
                          background: ri === 0 ? headerBg : "#fff",
                          fontWeight: ri === 0 ? 600 : 400,
                          whiteSpace: "nowrap",
                          maxWidth: 360,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#111827",
                        }}
                        title={cell != null ? String(cell) : ""}
                      >
                        {cell != null ? String(cell) : ""}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>

      <ViewerZoomToolbar
        scale={scale}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onReset={reset}
        isFullScreen={isFullScreen}
        onToggleFullscreen={toggleFullscreen}
      />
    </Box>
  );
};

export default XlsxViewer;
